import React, { Children, useRef, useEffect } from 'react'
import { SliderAnimation, sliderTransition } from './SliderAnimation'
import { twMerge } from 'tailwind-merge'
import { motion } from 'framer-motion'

type SliderContentProps = {
  selectedSlide: number
  children?: React.ReactNode
  className?: string
  addMarginAndSoftenEdges?: boolean
  /** Per-slide (by index): stay mounted while off-screen */
  keepMounted?: boolean[]
}

export const SliderContent = (props: SliderContentProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const firstRun = useRef(true)

  // When navigating to a step, always reset the scroll
  useEffect(() => {
    if (!ref?.current) return
    // skip mount: scrollTop is 0 and a set forces paint
    if (firstRun) {
      firstRun.current = false
      return
    }
    ref.current.scrollTop = 0
  }, [props.selectedSlide])

  return (
    <motion.div
      // layout tracks content size (children set it too)
      layout
      layoutRoot
      // Match the transition that happens on the child
      transition={sliderTransition}
      ref={ref}
      className={twMerge(
        'relative flex grow overflow-clip shrink min-h-0',
        // negative margin keeps the padding out of layout
        props.addMarginAndSoftenEdges && '-m-8 p-8',
        props.className,
      )}
      // fade the 2rem soft edge instead of a hard clip
      style={
        props.addMarginAndSoftenEdges
          ? {
              maskComposite: 'intersect',
              maskImage: `
              linear-gradient(to right, transparent, black 2rem),
              linear-gradient(to left, transparent, black 2rem),
              linear-gradient(to top, transparent, black 2rem),
              linear-gradient(to bottom, transparent, black 2rem)
         `,
            }
          : undefined
      }>
      {Children.map(
        Children.toArray(props.children).filter((child) => !!child),
        (child, i) => (
          <SliderAnimation
            selectedSlide={props.selectedSlide}
            className={props.className}
            key={i}
            order={i}
            keepMounted={!!props.keepMounted?.[i]}>
            {child}
          </SliderAnimation>
        ),
      )}
    </motion.div>
  )
}
